import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Toaster, configureToast } from "@guardian/shared-ui";
import { initializeSentry } from "./config/sentry.config";
import "./index.css";

// Configure toast behavior for this app
configureToast({ limit: 1, removeDelay: 1000000 });
import "./i18n";
import App from "./App";
import * as Sentry from "@sentry/react";

// Initialize Sentry with environment-specific configuration
initializeSentry();

// Sentry Error Boundary Fallback Component
const ErrorFallback = ({
  error,
  resetError
}: {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError: () => void;
}) => {
  const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";

  return (
    <div className="flex h-dvh w-full items-center justify-center bg-background">
      <div className="text-center space-y-4 p-6 max-w-md">
        <h1 className="text-2xl font-bold text-foreground">
          Something went wrong
        </h1>
        <p className="text-muted-foreground">
          {errorMessage}
        </p>
        <p className="text-sm text-muted-foreground">
          Our team has been notified. Please try reloading the page.
        </p>
        <button
          className="mt-4 rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90 transition-colors"
          onClick={resetError}
        >
          Reload application
        </button>
      </div>
    </div>
  );
}

const container = document.getElementById("root");
if (!container) {
  throw new Error("Failed to find the root element");
}

const root = createRoot(container);

root.render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <App />
      <Toaster />
    </Sentry.ErrorBoundary>
  </StrictMode>
);
